import {FC, ReactNode} from "react";
import {Dialog, IconButton} from "@mui/material";
import {styled} from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import {t} from "../../styles/theme/tokens";

/**
 * ============================================================================
 * BaseModal — структурная основа всех модальных окон Parallax.
 * ============================================================================
 *
 * Назначение: дать ЕДИНУЮ анатомию для всех диалогов, чтобы они перестали
 * быть "кто во что горазд". Поверх BaseModal строятся конкретные диалоги:
 *   - ConfirmDialog (подтверждения)
 *   - панели-списки (уведомления, биллинг)
 *   - формы в модалке (приглашение сотрудника)
 *
 * Анатомия:
 *   ┌────────────────────────────────────┐
 *   │ [icon] Заголовок              [✕]  │  ← header (h6, крестик)
 *   ├────────────────────────────────────┤
 *   │ children                           │  ← content (скроллится если длинный)
 *   │                                    │
 *   ├────────────────────────────────────┤
 *   │              [Отмена] [Действие]   │  ← actions (опц., выровнены вправо)
 *   └────────────────────────────────────┘
 *
 * Что взято из темы (не дублируем):
 *   - borderRadius 12, фон paper, тень — из theme.components.MuiDialog.
 *   - НЕ задаём borderRadius/padding на Paper вручную (старый ModalWindow
 *     перебивал тему хардкодом 6px).
 *
 * Размеры:
 *   - maxWidth: 'xs' | 'sm' | 'md' | 'lg' (как у MUI Dialog) ИЛИ число (px).
 *   - fullWidth по умолчанию true — диалог занимает maxWidth, не сжимается
 *     по контенту (предсказуемая ширина).
 *
 * Закрытие:
 *   - крестик, backdrop-клик, Escape — все закрывают (onClose).
 *   - hideCloseButton — убрать крестик (для случаев где закрытие только
 *     через кнопки actions).
 * ============================================================================
 */

export interface BaseModalProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    /** Иконка слева от заголовка (как колокольчик в окне уведомлений). */
    titleIcon?: ReactNode;
    children: ReactNode;
    /** Слот действий внизу (кнопки). Выровнены вправо. */
    actions?: ReactNode;
    /** Ширина: брейкпоинт MUI или число пикселей. По умолчанию 'sm' (~600px). */
    maxWidth?: "xs" | "sm" | "md" | "lg" | number;
    /** Скрыть крестик закрытия. */
    hideCloseButton?: boolean;
    /** Доп. контент в шапке справа (перед крестиком) — фильтры, счётчики. */
    headerExtra?: ReactNode;
}

const Header = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "20px 24px 12px",
    flexShrink: 0,
});

const TitleIconSlot = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: t.text.secondary,
    flexShrink: 0,

    "& > svg": {
        fontSize: 20,
    },
});

const TitleText = styled("h2")({
    fontSize: "1.125rem",          // 18px (h6)
    fontWeight: 600,
    lineHeight: 1.4,
    color: t.text.primary,
    margin: 0,
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

const HeaderExtraSlot = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
});

const Content = styled("div")({
    padding: "0 24px 8px",
    overflowY: "auto",
    // Контент скроллится внутри, шапка и actions остаются на месте.
    // max-height задаётся через Paper (см. PaperProps в Dialog ниже).
});

const Actions = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    padding: "12px 24px 20px",
    flexShrink: 0,
});

const BaseModal: FC<BaseModalProps> = ({
                                           open,
                                           onClose,
                                           title,
                                           titleIcon,
                                           children,
                                           actions,
                                           maxWidth = "sm",
                                           hideCloseButton = false,
                                           headerExtra,
                                       }) => {
    // maxWidth: число → задаём px через PaperProps; брейкпоинт → проп maxWidth.
    const isNumericWidth = typeof maxWidth === "number";

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={isNumericWidth ? false : maxWidth}
            aria-labelledby={title ? "modal-title" : undefined}
            slotProps={{
                paper: {
                    sx: {
                        // radius/фон/тень — из темы (MuiDialog). Здесь только
                        // структурные ограничения: ширина (если числовая) и
                        // max-height чтобы длинный контент скроллился внутри.
                        display: "flex",
                        flexDirection: "column",
                        ...(isNumericWidth && {width: maxWidth, maxWidth: "calc(100vw - 32px)"}),
                        maxHeight: "calc(100dvh - 64px)",
                    },
                },
            }}
        >
            {(title || !hideCloseButton || headerExtra) && (
                <Header>
                    {titleIcon && <TitleIconSlot>{titleIcon}</TitleIconSlot>}
                    {title && <TitleText id="modal-title">{title}</TitleText>}
                    {/* Если title нет — toolbar-row всё равно прижимает крестик вправо */}
                    {!title && <div style={{flex: 1}}/>}
                    {headerExtra && <HeaderExtraSlot>{headerExtra}</HeaderExtraSlot>}
                    {!hideCloseButton && (
                        <IconButton
                            aria-label="Закрыть"
                            onClick={onClose}
                            size="small"
                            sx={{color: t.text.secondary}}
                        >
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    )}
                </Header>
            )}

            <Content>{children}</Content>

            {actions && <Actions>{actions}</Actions>}
        </Dialog>
    );
};

export default BaseModal;